import { useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { get, isEmpty } from 'lodash';
import route from '../../constants/routes';
import { canDoProductionAction, getRouteWithCountryAndSlug, isOwner } from '../../utils/common';
import { createEntity, deleteEntity, updateOne, useBufferValueAction } from '../App/actions';
import { PRODUCTIONS } from '../../constants/endpointConstants';
import { MS_TO_DAY_DIVIDER, PRIVACY_ATTRIBUTE_IDS, PRODUCTION_ADDED_IDENTIFIERS_IDS } from '../../constants/consts';
import { EDITING_PRODUCTION_ENTITY_TYPES } from '../ProductionUpdateSteps/constants';

export const getCompanyRoute = company => getRouteWithCountryAndSlug(route.COMPANIES, company);

export const checkProductionAction = (permission, production, companyId) =>
  canDoProductionAction(permission, PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY, companyId, production);

export const errorMessageConverter = error => error?.responseText || error?.responseText?.message || error?.message;

function usePublishProduction(productionId, params, callback, onError) {
  const dispatch = useDispatch();

  return useCallback(() => {
    if (productionId) {
      dispatch(
        createEntity({
          endpoint: `${PRODUCTIONS}/${productionId}/actions`,
          sagaRoutine: useBufferValueAction,
          params: {
            type: 'publish',
            privacyAttribute: {
              id: PRIVACY_ATTRIBUTE_IDS.PUBLIC,
            },
            ...params,
          },
          callback,
          onError,
          errorMessageConverter,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionId, params, callback]);
}

export function usePublishCompanyProduction(productionId, companyId, callback, onError) {
  const params = {
    addedBy: { id: PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY },
    approvedBy: { id: PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY },
    company: {
      id: companyId,
    },
  };
  return usePublishProduction(productionId, params, callback, onError);
}

export function usePublishFestivalProduction(productionId, festivalId, callback, onError) {
  const params = {
    addedBy: { id: PRODUCTION_ADDED_IDENTIFIERS_IDS.FESTIVAL },
    approvedBy: { id: PRODUCTION_ADDED_IDENTIFIERS_IDS.FESTIVAL },
    festival: {
      id: festivalId,
    },
  };
  return usePublishProduction(productionId, params, callback, onError);
}

export function usePublishArtistProduction(productionId, artistId, callback, onError) {
  const params = {
    addedBy: { id: PRODUCTION_ADDED_IDENTIFIERS_IDS.ARTIST },
    profile: {
      id: artistId,
    },
  };
  return usePublishProduction(productionId, params, callback, onError);
}

/**
 * Returns publish production hook by entity type
 * @param entityType
 * @returns {(function(*=, *=, *=, *=): function(): void)|null}
 */
export function usePublishProductionFactory(entityType) {
  switch (entityType) {
    case EDITING_PRODUCTION_ENTITY_TYPES.COMPANY:
      return usePublishCompanyProduction;
    case EDITING_PRODUCTION_ENTITY_TYPES.FESTIVAL:
      return usePublishFestivalProduction;
    case EDITING_PRODUCTION_ENTITY_TYPES.ARTIST:
      return usePublishArtistProduction;
    default:
      return null;
  }
}

export function useDeleteProduction(production, company, callback) {
  const dispatch = useDispatch();
  const updateProduction = useUpdateProduction();
  const isPublished = Boolean(production?.published_at);

  return useCallback(() => {
    if (production) {
      const isOwnerCompany = isOwner(production, PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY, company?.id);
      if (!isPublished || isOwnerCompany) {
        dispatch(
          deleteEntity({
            endpoint: PRODUCTIONS,
            id: production?.id,
            callback,
          }),
        );
      } else if (isPublished || !isOwnerCompany) {
        updateProduction(production?.id, {
          companies: production?.companies?.filter(c => c?.id !== company?.id),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [production, company, callback]);
}

export function useUpdateProduction(callback) {
  const dispatch = useDispatch();
  return (productionId, options) => {
    dispatch(
      updateOne({
        endpoint: PRODUCTIONS,
        id: productionId,
        params: options,
        sagaRoutine: useBufferValueAction,
        callback,
      }),
    );
  };
}

export const DELETION_DAYS_LIMIT = 10;

export function useProductionDeleteDaysLimit(publishedDate) {
  return useMemo(() => {
    const today = +new Date();
    const publishDate = +new Date(publishedDate);
    const numOfDaysAfterPublishing = (today - publishDate) / MS_TO_DAY_DIVIDER;
    return numOfDaysAfterPublishing > DELETION_DAYS_LIMIT;
  }, [publishedDate]);
}

export const PRIVACY_ENTITY_TYPES = {
  PROFILE: 'profile',
  COMPANY: 'company',
  FESTIVAL: 'festival',
};

export function useIsHidden(production, type, id) {
  return useMemo(() => {
    if (!Array.isArray(production?.privacy)) {
      return false;
    }
    return (
      production.privacy.find(el => get(el, `${type}Id`) === id)?.privacyAttribute?.id === PRIVACY_ATTRIBUTE_IDS.PRIVATE
    );
  }, [id, production, type]);
}

export function useIsHiddenForProfile(production, id) {
  return useIsHidden(production, PRIVACY_ENTITY_TYPES.PROFILE, id);
}

export function useIsHiddenForCompany(production, id) {
  return useIsHidden(production, PRIVACY_ENTITY_TYPES.COMPANY, id);
}

export function useIsAcknowledged(production, type, id) {
  return useMemo(() => {
    if (!Array.isArray(production?.privacy)) {
      return true;
    }

    const matchingPrivacyEntity = production?.privacy?.find(el => get(el, `${type}Id`) === id);

    if (isEmpty(matchingPrivacyEntity)) {
      return true;
    }

    return matchingPrivacyEntity.acknowledged;
  }, [id, production, type]);
}

export function useIsAcknowledgedForProfile(production, id) {
  return useIsAcknowledged(production, PRIVACY_ENTITY_TYPES.PROFILE, id);
}
